import { useCallback, useEffect, useState } from "react";
import { Loader2, Send, UtensilsCrossed, BedDouble, Sparkles, CalendarDays, User, Phone } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { HotelSelect } from "../components/HotelSelect";
import { SmileyRating } from "../components/SmileyRating";
import { AudioDropzone } from "../components/AudioDropzone";
import { SuccessDialog } from "../components/SuccessDialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg, todayLocal } from "../lib/api";
import { COUNTRY_CODES, formatMobile, slug } from "../lib/constants";

const CATS = [
  { v: "Restaurant", icon: UtensilsCrossed },
  { v: "Room Stay", icon: BedDouble },
  { v: "Both", icon: Sparkles },
];
const blank = () => ({ guest_name: "", visit_date: todayLocal(), code: "+91", mobile: "", category: "Restaurant", rating: 0, remarks: "" });

const Section = ({ n, title, children, className = "" }) => (
  <section className={`glass rounded-3xl p-5 sm:p-7 rise ${className}`}>
    <div className="flex items-center gap-3 mb-5">
      <span className="h-7 w-7 rounded-full bg-amber-400/90 text-emerald-950 text-xs font-extrabold grid place-items-center">{n}</span>
      <h3 className="text-lg font-bold text-emerald-950">{title}</h3>
    </div>
    {children}
  </section>
);

export default function FeedbackForm() {
  const [hotel, setHotelState] = useState(localStorage.getItem("tamanna_hotel") || "");
  const [f, setF] = useState(blank);
  const [audio, setAudio] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [dup, setDup] = useState(null);
  const [critical, setCritical] = useState(false);
  const localDigits = f.mobile.replace(/\D/g, "");
  const mobileOk = f.code === "+91" ? localDigits.length === 10 : localDigits.length >= 6 && localDigits.length <= 12;

  useEffect(() => {
    setDup(null);
    setCritical(false);
    if (!mobileOk) return undefined;
    let live = true;
    const full = `${f.code}${localDigits}`;
    const t = setTimeout(() => {
      api.get("/critical-guests/check", { params: { mobile: full } })
        .then((r) => { if (live) setCritical(r.data.blocked); })
        .catch(() => {});
      if (hotel) {
        api.get("/feedback/by-mobile", { params: { mobile: full, hotel } })
          .then((r) => { if (live) setDup(r.data.find((x) => x.mobile_digits === full.replace(/\D/g, "")) || null); })
          .catch(() => {});
      }
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [hotel, f.code, localDigits, mobileOk]);
  const setHotel = (h) => { setHotelState(h); localStorage.setItem("tamanna_hotel", h); };

  const reset = useCallback(() => { setDone(null); setF(blank()); setAudio(null); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!hotel) return toast.error("Select the hotel property first");
    if (!f.guest_name.trim()) return toast.error("Guest name is required");
    if (!localDigits) return toast.error("Mobile number is required");
    if (!mobileOk) return toast.error(f.code === "+91" ? "Enter a valid 10-digit mobile number" : "Enter a valid mobile number");
    if (critical) return toast.error("This number belongs to a Critical guest. Feedback cannot be taken.");
    if (dup) return toast.error(`This number already gave feedback at ${hotel}`);
    if (!f.rating) return toast.error("Tap a smiley to rate the experience");
    if (!audio) return toast.error("Upload the guest voice recording");
    const fd = new FormData();
    fd.append("hotel", hotel);
    fd.append("guest_name", f.guest_name);
    fd.append("visit_date", f.visit_date);
    fd.append("mobile", `${f.code} ${f.mobile}`);
    fd.append("category", f.category);
    fd.append("rating", f.rating);
    fd.append("remarks", f.remarks);
    if (audio) fd.append("audio", audio);
    setBusy(true);
    try {
      await api.post("/feedback", fd);
      setDone(f.guest_name.split(" ")[0]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen">
      <AppHeader>
        <div className="hidden sm:block"><HotelSelect value={hotel} onChange={setHotel} testId="header-property-selector" /></div>
      </AppHeader>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8 rise">
          <div className="eyebrow">Guest feedback desk</div>
          <h1 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-emerald-950">How was the stay?</h1>
          <p className="mt-3 text-slate-600 max-w-xl">Fill in what the guest shares. Takes under a minute — the form resets for the next guest.</p>
        </div>
        <form onSubmit={submit} className="grid gap-5" data-testid="feedback-form">
          <Section n="1" title="Property & guest" className="d1">
            <div className="sm:hidden mb-4">
              <label className="field-label">Hotel property *</label>
              <div className="mt-2"><HotelSelect value={hotel} onChange={setHotel} testId="form-property-selector" /></div>
            </div>
            <div className="hidden sm:flex flex-wrap gap-2 mb-5" data-testid="hotel-chips">
              {["Grand Tamanna", "Premier Tamanna", "Bizz Tamanna", "Executive Tamanna", "Shree Inn by Tamanna Hotels", "Imperial Tamanna"].map((h) => (
                <button type="button" key={h} data-testid={`hotel-select-${slug(h)}`} onClick={() => setHotel(h)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${hotel === h ? "bg-emerald-900 text-amber-50 border-emerald-900" : "bg-white/80 text-emerald-900 border-emerald-900/15 hover:border-emerald-700"}`}>
                  {h}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="field-label">Guest name *</label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input data-testid="guest-name-input" value={f.guest_name} onChange={(e) => set("guest_name", e.target.value)} placeholder="e.g. Priya Sharma" className="h-12 pl-10 rounded-xl bg-white text-base" />
                </div>
              </div>
              <div>
                <label className="field-label">Date of visit / stay</label>
                <div className="relative mt-2">
                  <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input data-testid="visit-date-input" type="date" max={todayLocal()} value={f.visit_date} onChange={(e) => set("visit_date", e.target.value)} className="h-12 pl-10 rounded-xl bg-white text-base" />
                </div>
              </div>
              <div>
                <label className="field-label">Mobile number *</label>
                <div className="mt-2 flex gap-2">
                  <Select value={f.code} onValueChange={(v) => setF((p) => ({ ...p, code: v, mobile: formatMobile(v, p.mobile) }))}>
                    <SelectTrigger data-testid="country-code-select" className="h-12 w-[92px] rounded-xl bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {COUNTRY_CODES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input data-testid="guest-mobile-input" inputMode="tel" required value={f.mobile} onChange={(e) => set("mobile", formatMobile(f.code, e.target.value))} placeholder={f.code === "+91" ? "98765 43210" : "Phone number"} className={`h-12 pl-10 rounded-xl bg-white text-base ${dup || critical ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  </div>
                </div>
                {critical && (
                  <p data-testid="mobile-critical-guest-error" className="mt-2 text-xs font-semibold text-red-600">
                    This number belongs to a Critical guest. Feedback cannot be taken from this number.
                  </p>
                )}
                {!critical && dup && (
                  <p data-testid="mobile-duplicate-warning" className="mt-2 text-xs font-semibold text-red-600">
                    Already gave feedback at {hotel} on {dup.visit_date} ({dup.guest_name}). Duplicate entries are not allowed.
                  </p>
                )}
              </div>
            </div>
          </Section>

          <Section n="2" title="Service & rating" className="d2">
            <label className="field-label">Sales category</label>
            <div className="mt-2 mb-6 grid grid-cols-3 gap-1 rounded-2xl bg-emerald-900/5 p-1" data-testid="category-segment">
              {CATS.map(({ v, icon: Icon }) => (
                <button type="button" key={v} data-testid={`category-${slug(v)}-button`} onClick={() => set("category", v)}
                  className={`h-12 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${f.category === v ? "bg-white text-emerald-950 shadow-md" : "text-emerald-900/60 hover:text-emerald-900"}`}>
                  <Icon className="h-4 w-4" /> {v}
                </button>
              ))}
            </div>
            <label className="field-label">Overall rating *</label>
            <div className="mt-3"><SmileyRating value={f.rating} onChange={(v) => set("rating", v)} /></div>
          </Section>

          <div className="grid lg:grid-cols-2 gap-5">
            <Section n="3" title="Guest voice recording *" className="d3">
              <AudioDropzone file={audio} onChange={setAudio} />
            </Section>
            <Section n="4" title="Remarks" className="d4">
              <Textarea data-testid="remarks-textarea" value={f.remarks} onChange={(e) => set("remarks", e.target.value)} rows={5} placeholder="Anything the guest mentioned — praise, complaints, requests…" className="rounded-xl bg-white text-base min-h-[140px]" />
            </Section>
          </div>

          <div className="flex justify-end rise d5">
            <button data-testid="feedback-submit-button" disabled={busy} className="h-14 px-10 rounded-full bg-emerald-900 text-amber-50 text-base font-bold shadow-xl shadow-emerald-900/20 hover:bg-emerald-800 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60 flex items-center gap-3">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Submit Feedback
            </button>
          </div>
        </form>
      </main>
      <SuccessDialog open={!!done} guest={done} onDone={reset} />
    </div>
  );
}
